import type { EmailBlastViewDto } from '@/types/emailBlast'
import { countRecipients } from './spine/blastVerdict'
import { SectionHeading, Stat } from './sectionChrome'

/**
 * What happened to the mail (ORB-706; was SendStatsSection). The headline half — how far through, and
 * whether anything is about to stop it — moved up into the status spine, so what remains here is the
 * audience funnel: the accounting an operator drops to when the spine's numbers prompt a "why".
 *
 * Reads left-to-right: imported → excluded → [registered after] → recipients → outcomes, so a drop
 * between the audience and what shipped explains itself.
 */
export function DeliverySection({ blast }: { blast: EmailBlastViewDto }) {
  return (
    <section className="space-y-3">
      <SectionHeading>Delivery</SectionHeading>
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat
          label="Audience"
          value={blast.TotalAudience + blast.ExcludedCount}
          hint="Distinct people in the imported audience, before exclusions."
        />
        <Stat
          label="Excluded"
          value={blast.ExcludedCount}
          hint="Audience members not sent to: already converted, suppressed (opt-out / hard bounce), invalid, or duplicate."
        />
        {blast.PulledAfterRegistrationCount > 0 && (
          <Stat
            label="Registered after"
            value={blast.PulledAfterRegistrationCount}
            hint="Registered during the reminder window and pulled from this send — so a registrant never gets a 'please register' nudge."
          />
        )}
        {/* Shares the spine's count so the page never shows two different "Recipients". Subtracting
            PulledAfterRegistration here would double-count it: those rows keep Status = Scheduled with
            Cancelled = true, so they already sit inside Skipped. */}
        <Stat
          label="Recipients"
          value={countRecipients(blast)}
          hint="Distinct recipient rows this blast holds. Anyone who registered after staging is counted here and again under Skipped, which is what pulled them."
        />
        <Stat
          label="Queued"
          value={blast.Stats.Queued}
          hint="Scheduled and not yet sent. Cancelled rows are counted under Skipped, not here."
        />
        <Stat label="Sent" value={blast.Stats.Sent} hint="Rows that have ever reached the provider." />
        <Stat label="Delivered" value={blast.Stats.Delivered} />
        <Stat label="Bounced" value={blast.Stats.Bounced} tone={blast.Stats.Bounced > 0 ? 'warn' : undefined} />
        <Stat label="Failed" value={blast.Stats.Failed} tone={blast.Stats.Failed > 0 ? 'danger' : undefined} />
      </dl>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        <span
          title="Recipients cancelled before send: blocked by the environment guard, suppressed (hard bounce / opt-out), or dropped by the blast circuit breaker. Not delivery failures."
          className="cursor-help underline decoration-dotted underline-offset-2"
        >
          Skipped
        </span>{' '}
        {blast.Stats.Skipped.toLocaleString()}
        {' · '}
        <span className={blast.Stats.Complained > 0 ? 'text-amber-600 dark:text-amber-400' : undefined}>
          Complained {blast.Stats.Complained.toLocaleString()}
        </span>
        {blast.Stats.SoftBounced > 0 && (
          <>
            {' · '}
            <span
              title="Temporary rejections the provider may accept later. Diagnostic only — they never trip the circuit breaker."
              className="cursor-help underline decoration-dotted underline-offset-2"
            >
              Soft bounced
            </span>{' '}
            {blast.Stats.SoftBounced.toLocaleString()}
          </>
        )}
      </p>
    </section>
  )
}
